import React from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';

const HomePage = () => {
  const features = [
    {
      title: 'Censorship-Resistant',
      description: 'No central authority can delete or alter your petition once it is on the blockchain. Your voice is permanently protected.',
      image: '/images/Censorship-Resistant.jpg'
    },
    {
      title: 'Community Owned',
      description: 'Petition.io returns nearly all value — over 97% — back to campaigns, communities, and humanitarian impact.',
      image: '/images/Community-Owned.jpg'
    },
    {
       title: 'Fully Transparent',
      description: 'Every signature, vote, and contribution is publicly verifiable on the blockchain. Complete transparency guaranteed.',
      image: '/images/Fully-Transparent.jpg'
    }
  ];

  const useCases = [
    {
      title: 'Social Justice',
      description: 'Rally support for human rights, equality, and justice worldwide',
      image: '/images/Social-justice.jpg',
    },
    {
      title: 'Environmental Action',
      description: 'Protect our planet and drive climate change initiatives',
      image: '/images/Environmental-Action.jpg',
    },
    {
      title: 'Political Reform',
      description: 'Demand transparency and accountability from governments',
      image: '/images/Political-Reform.jpg',
    },
    {
      title: 'Fund Campaigns',
      description: 'Support causes with transparent blockchain-based funding',
      image: '/images/earn-eth.jpg',
    }
  ];

  return (
    <>
      <Helmet>
        <title>Petition.io - Decentralized Petition Platform | Blockchain Petitions on Ethereum | Web3 Activism</title>
        <meta name="description" content="The world's first fully decentralized petition platform powered by Ethereum blockchain. Create, sign, and fund censorship-resistant petitions that can never be deleted, manipulated, or censored. Join 10,000+ activists fighting for social justice, environmental protection, political reform, and human rights worldwide." />
        <meta name="keywords" content="blockchain petitions, decentralized petitions, ethereum dapp, web3 petitions, crypto activism, censorship resistant, immutable petitions, DAO governance, smart contract petitions, decentralized advocacy, petition platform, online petitions, digital activism, social change, grassroots movements, community governance, transparent voting, verifiable signatures, permanent records, unstoppable petitions, crypto for good, blockchain activism, web3 social impact, decentralized democracy, petition funding, crowdfunding petitions, ethereum smart contracts, polygon petitions, crypto donations" />
        <meta name="author" content="Petition.io - Decentralized Petition Platform" />
        <meta name="robots" content="index, follow, max-snippet:-1, max-image-preview:large, max-video-preview:-1" />
        <link rel="canonical" href="https://petition.io/" />
        
        {/* Open Graph / Facebook */}
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://petition.io/" />
        <meta property="og:title" content="Petition.io - Decentralized Petition Platform on Ethereum Blockchain" />
        <meta property="og:description" content="Create unstoppable petitions for social justice, environmental action, political reform, and human rights. Powered by blockchain technology. Censorship-resistant. Fully transparent. Community owned." />
        <meta property="og:image" content="https://petition.io/images/petition.png" />
        <meta property="og:image:width" content="1200" />
        <meta property="og:image:height" content="630" />
        <meta property="og:site_name" content="Petition.io" />
        <meta property="og:locale" content="en_US" />
        
        {/* Twitter */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:url" content="https://petition.io/" />
        <meta name="twitter:title" content="Petition.io - Blockchain Petition Platform | Unstoppable Activism" />
        <meta name="twitter:description" content="The world's first fully decentralized petition platform. Create censorship-resistant petitions on Ethereum. Your voice, unstoppable." />
        <meta name="twitter:image" content="https://petition.io/images/petition.png" />
        <meta name="twitter:site" content="@PetitionIO" />
        <meta name="twitter:creator" content="@PetitionIO" />
        
        {/* Geographic SEO - Global Coverage */}
        <meta name="geo.region" content="US;GB;CA;AU;IN;DE;FR;JP;BR;MX" />
        <meta name="geo.placename" content="Global, Worldwide" />
        <meta name="geo.position" content="0;0" />
        <meta name="ICBM" content="0, 0" />
        
        {/* Additional SEO */}
        <meta name="language" content="English" />
        <meta name="revisit-after" content="3 days" />
        <meta name="distribution" content="global" />
        <meta name="rating" content="general" />
        <meta name="coverage" content="Worldwide" />
        <meta name="target" content="all" />
        <meta name="audience" content="all" />
        <meta name="classification" content="Petition Platform, Blockchain, Web3, Social Impact, Activism" />
        
        {/* Structured Data - WebApplication */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "WebApplication",
            "name": "Petition.io",
            "alternateName": "Petition IO",
            "description": "Decentralized petition platform built on Ethereum blockchain for censorship-resistant activism and social change",
            "url": "https://petition.io",
            "applicationCategory": "SocialNetworkingApplication",
            "operatingSystem": "Web Browser, iOS, Android",
            "offers": {
              "@type": "Offer",
              "price": "0",
              "priceCurrency": "USD"
            },
            "aggregateRating": {
              "@type": "AggregateRating",
              "ratingValue": "4.8",
              "ratingCount": "2547"
            },
            "author": {
              "@type": "Organization",
              "name": "Petition.io",
              "url": "https://petition.io",
              "logo": "https://petition.io/images/petition.png",
              "sameAs": [
                "https://x.com/PetitionIO",
                "https://www.linkedin.com/company/petitionio",
                "https://discord.gg/petitionio",
                "https://t.me/petitionio"
              ]
            },
            "potentialAction": {
              "@type": "SearchAction",
              "target": "https://petition.io/search?q={search_term_string}",
              "query-input": "required name=search_term_string"
            }
          })}
        </script>
        
        {/* Structured Data - Organization */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Organization",
            "name": "Petition.io",
            "url": "https://petition.io",
            "logo": "https://petition.io/images/petition.png",
            "description": "The world's first fully decentralized petition platform powered by Ethereum blockchain",
            "foundingDate": "2024",
            "founders": [{
              "@type": "Organization",
              "name": "Petitioner DAO"
            }],
            "sameAs": [
              "https://x.com/PetitionIO",
              "https://www.linkedin.com/company/petitionio",
              "https://discord.gg/petitionio",
              "https://t.me/petitionio"
            ],
            "contactPoint": {
              "@type": "ContactPoint",
              "contactType": "Customer Support",
              "url": "https://petition.io/contact"
            }
          })}
        </script>
      </Helmet>

      <div className="min-h-screen">
        {/* Hero Section - Centered Layout with Background Video */}
        <section className="relative min-h-screen flex items-center justify-center pt-20">
          {/* Background GIF with Overlay */}
          <div className="absolute inset-0">
            <img 
              src="/images/hero-clean.gif"
              alt="Petition.io Hero Background"
              className="w-full h-full object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-b from-black/60 via-black/50 to-black/70"></div>
          </div>

          {/* Centered Content */}
          <div className="relative z-10 max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-12 sm:py-16 md:py-20 text-center">
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ delay: 0.2 }}
              className="inline-flex items-center space-x-2 px-4 sm:px-6 py-2 sm:py-3 bg-white/10 backdrop-blur-sm border border-white/20 rounded-full mb-6 sm:mb-8"
            >
              <span className="w-2 h-2 bg-white rounded-full animate-pulse"></span>
              <span className="text-xs sm:text-sm font-medium text-white font-mono">Built on Ethereum Blockchain</span>
            </motion.div>

            <motion.h1
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3 }}
              className="text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-bold font-lato leading-tight text-white mb-6"
            >
              Create and Fund Petitions That
              <br />
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-400 to-cyan-400">
                Can't Be Silenced
              </span>
            </motion.h1>

            <motion.p
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.4 }}
              className="text-xl sm:text-2xl md:text-3xl font-semibold mb-4 sm:mb-6 leading-relaxed font-montserrat text-white"
            >
              The world's first blockchain-powered petition-based crowdfunding platform.
            </motion.p>

            <motion.p
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.5 }}
              className="text-lg sm:text-xl md:text-2xl mb-8 sm:mb-12 leading-relaxed font-montserrat text-white/90 max-w-4xl mx-auto"
            >
              Your petitions secured permanently on Ethereum—empowering lasting change and amplifying voices worldwide.
            </motion.p>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.6 }}
              className="flex flex-col sm:flex-row gap-4 sm:gap-6 justify-center"
            >
              <a href="https://app.petition.io" target="_blank" rel="noopener noreferrer">
                <motion.button
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  className="px-8 sm:px-12 py-4 sm:py-5 text-lg sm:text-xl font-bold font-montserrat rounded-full transition-all inline-flex items-center justify-center shadow-2xl"
                  style={{
                    backgroundColor: '#f97316',
                    color: '#ffffff',
                    textTransform: 'uppercase'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.backgroundColor = '#ea580c';
                    e.currentTarget.style.transform = 'scale(1.05)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.backgroundColor = '#f97316';
                    e.currentTarget.style.transform = 'scale(1)';
                  }}
                >
                  <span>Launch dApp</span>
                  <svg className="ml-3 w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M13 7l5 5m0 0l-5 5m5-5H6" />
                  </svg>
                </motion.button>
              </a>
              <Link to="/how-it-works">
                <motion.button
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  className="px-8 sm:px-12 py-4 sm:py-5 text-lg sm:text-xl font-semibold font-montserrat rounded-full border-2 transition-all duration-300 inline-flex items-center justify-center shadow-2xl backdrop-blur-sm"
                  style={{
                    borderColor: '#ffffff',
                    backgroundColor: 'rgba(255, 255, 255, 0.1)',
                    color: '#ffffff',
                    textTransform: 'uppercase'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.backgroundColor = '#ffffff';
                    e.currentTarget.style.color = '#3498db';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.backgroundColor = 'rgba(255, 255, 255, 0.1)';
                    e.currentTarget.style.color = '#ffffff';
                  }}
                >
                  <svg className="mr-2 w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  <span>How It Works</span>
                </motion.button>
              </Link>
            </motion.div>
          </div>
        </section>

        {/* COMMENTED OUT: Original Two-Column Hero Section
        <section className="relative min-h-screen flex items-center pt-20 sm:pt-24 md:pt-28" style={{ 
          background: 'linear-gradient(135deg, #DADADA 0%, #E8E8E8 30%, #F0F0F0 70%, #DADADA 100%)'
        }}>
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 sm:py-16 md:py-20 w-full">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-12 items-center">
              <motion.div
                initial={{ opacity: 0, x: -30 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.8 }}
                className="order-2 lg:order-1"
              >
                ...original content...
              </motion.div>
              <motion.div
                initial={{ opacity: 0, x: 30 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.8, delay: 0.3 }}
                className="order-1 lg:order-2"
              >
                ...original GIF...
              </motion.div>
            </div>
          </div>
        </section>
        */}

        {/* Why Blockchain Petitions Matter */}
        <section className="pt-32 pb-16 md:pb-24 bg-gradient-to-br from-gray-50 via-white to-blue-50/30">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            {/* Section Header */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="text-center mb-12 md:mb-16"
            >
              <h2 className="text-2xl md:text-3xl lg:text-4xl font-bold font-lato mb-4" style={{ color: 'var(--petition-dark)' }}>
                Why Blockchain <span className="text-transparent bg-clip-text bg-gradient-to-r from-primary-blue to-primary-light">Changes Everything</span>
              </h2>
              <p className="text-base md:text-lg text-gray-600 max-w-3xl mx-auto">
                The world's first fully decentralized petition platform built on Ethereum
              </p>
            </motion.div>

            {/* Main Content Grid */}
            <div className="grid lg:grid-cols-2 gap-8 lg:gap-12 items-center mb-16">
              {/* Left: Image + Description */}
              <motion.div
                initial={{ opacity: 0, x: -30 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                className="space-y-6"
              >
                {/* Feature Image */}
                <div className="relative rounded-2xl overflow-hidden shadow-2xl group">
                  <img 
                    src="/images/image7.jpg" 
                    alt="Blockchain Powered Petitions" 
                    className="w-full h-64 md:h-80 lg:h-96 object-cover group-hover:scale-105 transition-transform duration-500"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/30 to-transparent"></div>
                  <div className="absolute bottom-0 left-0 right-0 p-6 text-white">
                    <div className="flex items-center gap-2 mb-3">
                      <div className="w-3 h-3 bg-green-400 rounded-full animate-pulse"></div>
                      <span className="text-sm font-semibold uppercase tracking-wide">Live on Ethereum</span>
                    </div>
                    <h3 className="text-xl md:text-2xl font-bold mb-2 text-white">Unstoppable by Design</h3>
                    <p className="text-sm md:text-base text-white/90">Every petition secured by blockchain technology</p>
                  </div>
                </div>

                {/* Info Text */}
                <div className="bg-white p-6 md:p-8 rounded-xl shadow-lg border border-gray-100">
                  <p className="text-base md:text-lg text-gray-700 leading-relaxed mb-4">
                    <strong className="text-ethereum">Petition.io</strong> is the world's first fully decentralized petition platform. We leverage Ethereum blockchain and smart contracts to create an unstoppable platform where your voice truly matters.
                  </p>
                  <p className="text-sm md:text-base text-gray-600 leading-relaxed">
                    Create, sign, and fund petitions that can <strong>never be deleted, manipulated, or censored</strong>. Once published, nobody—not governments, corporations, or even our team—can alter or remove your cause.
                  </p>
                </div>
              </motion.div>

              {/* Right: Blockchain Features Grid */}
              <motion.div
                initial={{ opacity: 0, x: 30 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                className="grid gap-4 md:gap-6"
              >
                <h3 className="text-xl md:text-2xl font-bold text-neutral-dark mb-2">
                  Powered by Blockchain Technology
                </h3>

                {/* Feature Cards */}
                {[
                  {
                    icon: (
                      <svg className="w-8 h-8" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M2.166 4.999A11.954 11.954 0 0010 1.944 11.954 11.954 0 0017.834 5c.11.65.166 1.32.166 2.001 0 5.225-3.34 9.67-8 11.317C5.34 16.67 2 12.225 2 7c0-.682.057-1.35.166-2.001zm11.541 3.708a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                      </svg>
                    ),
                    title: 'Immutable Records',
                    description: 'Stored permanently on Ethereum blockchain',
                    color: 'from-blue-500 to-blue-600'
                  },
                  {
                    icon: (
                      <svg className="w-8 h-8" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M6.267 3.455a3.066 3.066 0 001.745-.723 3.066 3.066 0 013.976 0 3.066 3.066 0 001.745.723 3.066 3.066 0 012.812 2.812c.051.643.304 1.254.723 1.745a3.066 3.066 0 010 3.976 3.066 3.066 0 00-.723 1.745 3.066 3.066 0 01-2.812 2.812 3.066 3.066 0 00-1.745.723 3.066 3.066 0 01-3.976 0 3.066 3.066 0 00-1.745-.723 3.066 3.066 0 01-2.812-2.812 3.066 3.066 0 00-.723-1.745 3.066 3.066 0 010-3.976 3.066 3.066 0 00.723-1.745 3.066 3.066 0 012.812-2.812zm7.44 5.252a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                      </svg>
                    ),
                    title: 'Verified Signatures',
                    description: 'Cryptographically authenticated via wallet addresses',
                    color: 'from-purple-500 to-purple-600'
                  },
                  {
                    icon: (
                      <svg className="w-8 h-8" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M18 8a6 6 0 01-7.743 5.743L10 14l-1 1-1 1H6v2H2v-4l4.257-4.257A6 6 0 1118 8zm-6-4a1 1 0 100 2 2 2 0 012 2 1 1 0 102 0 4 4 0 00-4-4z" clipRule="evenodd" />
                      </svg>
                    ),
                    title: 'Privacy Protected',
                    description: 'Pseudonymous participation, no personal data required',
                    color: 'from-indigo-500 to-indigo-600'
                  },
                  {
                    icon: (
                      <svg className="w-8 h-8" fill="currentColor" viewBox="0 0 20 20">
                        <path d="M13 6a3 3 0 11-6 0 3 3 0 016 0zM18 8a2 2 0 11-4 0 2 2 0 014 0zM14 15a4 4 0 00-8 0v3h8v-3zM6 8a2 2 0 11-4 0 2 2 0 014 0zM16 18v-3a5.972 5.972 0 00-.75-2.906A3.005 3.005 0 0119 15v3h-3zM4.75 12.094A5.973 5.973 0 004 15v3H1v-3a3 3 0 013.75-2.906z" />
                      </svg>
                    ),
                    title: 'DAO Governance',
                    description: 'Community-driven decisions, 97% public ownership',
                    color: 'from-orange-500 to-orange-600'
                  }
                ].map((feature, idx) => (
                  <motion.div
                    key={idx}
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ delay: idx * 0.1 }}
                    whileHover={{ scale: 1.02, x: 5 }}
                    className="group bg-white p-5 md:p-6 rounded-xl shadow-lg hover:shadow-2xl transition-all duration-300 border border-gray-100 hover:border-primary-blue/30"
                  >
                    <div className="flex items-start gap-4">
                      <div className={`w-14 h-14 md:w-16 md:h-16 rounded-xl bg-gradient-to-br ${feature.color} flex items-center justify-center flex-shrink-0 text-white shadow-lg group-hover:scale-110 transition-transform`}>
                        {feature.icon}
                      </div>
                      <div className="flex-1 min-w-0">
                        <h4 className="text-lg md:text-xl font-bold text-neutral-dark mb-2 group-hover:text-primary-blue transition-colors">
                          {feature.title}
                        </h4>
                        <p className="text-sm md:text-base text-gray-600 leading-relaxed">
                          {feature.description}
                        </p>
                      </div>
                    </div>
                  </motion.div>
                ))}
              </motion.div>
            </div>

            {/* Bottom CTA Banner */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="bg-gradient-to-r from-primary-blue via-blue-600 to-primary-light p-6 md:p-8 rounded-2xl text-white text-center shadow-2xl"
            >
              <h3 className="text-2xl md:text-3xl font-bold font-lato mb-3" style={{ color: '#ffffff' }}>Create, Sign & Fund Petitions That Never Disappear</h3>
              <p className="text-base md:text-lg text-white/90 mb-6 max-w-3xl mx-auto">
                Built on Ethereum blockchain with smart contracts ensuring transparency, immutability, and global accessibility
              </p>
              <Link to="/how-it-works">
                <motion.button
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  className="px-6 md:px-8 py-3 md:py-4 bg-white text-primary-blue font-bold rounded-xl shadow-lg hover:shadow-xl transition-all text-sm md:text-base"
                >
                  Learn How It Works →
                </motion.button>
              </Link>
            </motion.div>
          </div>
        </section>

        <section className="pt-32 pb-20 bg-gradient-to-br from-white via-blue-50/30 to-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="text-center mb-16"
            >
              <h2 className="text-4xl md:text-5xl font-bold font-lato mb-4" style={{ color: 'var(--petition-dark)' }}>
                Why Choose <span 
                  style={{ 
                    background: `linear-gradient(135deg, var(--petition-primary) 0%, var(--petition-secondary) 100%)`,
                    WebkitBackgroundClip: 'text',
                    WebkitTextFillColor: 'transparent',
                    backgroundClip: 'text'
                  }}
                >Petition.io</span>?
              </h2>
              <p className="text-xl font-montserrat" style={{ color: '#6b7280' }}>Built on blockchain technology for maximum security and transparency</p>
            </motion.div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              {features.map((feature, idx) => (
                <motion.div
                  key={idx}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: idx * 0.1 }}
                  whileHover={{ y: -5, boxShadow: '0 20px 40px rgba(0, 102, 204, 0.2)' }}
                  className="bg-white rounded-2xl shadow-lg overflow-hidden group"
                >
                  <div className="relative h-56 overflow-hidden">
                    <img 
                      src={feature.image} 
                      alt={feature.title}
                      className="w-full h-full object-cover transform group-hover:scale-110 transition-transform duration-500"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent"></div>
                    <div className="absolute bottom-4 left-4 text-white">
                      {feature.icon}
                    </div>
                  </div>
                  <div className="p-8">
                    <h3 className="text-2xl font-bold text-neutral-dark mb-3">{feature.title}</h3>
                    <p className="text-gray-600 leading-relaxed">{feature.description}</p>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        <section className="pt-16 pb-20 bg-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="text-center mb-16"
            >
              <h2 className="text-4xl md:text-5xl font-bold font-lato mb-4" style={{ color: 'var(--petition-dark)' }}>
                Create Impact in <span 
                  style={{ 
                    background: `linear-gradient(135deg, var(--petition-primary) 0%, var(--petition-secondary) 100%)`,
                    WebkitBackgroundClip: 'text',
                    WebkitTextFillColor: 'transparent',
                    backgroundClip: 'text'
                  }}
                >Any Area</span>
              </h2>
              <p className="text-xl font-montserrat" style={{ color: '#6b7280' }}>From social justice to environmental action, your voice matters</p>
            </motion.div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              {useCases.map((useCase, idx) => (
                <motion.div
                  key={idx}
                  initial={{ opacity: 0, scale: 0.9 }}
                  whileInView={{ opacity: 1, scale: 1 }}
                  viewport={{ once: true }}
                  transition={{ delay: idx * 0.1 }}
                  whileHover={{ scale: 1.05, y: -5 }}
                  className="relative rounded-2xl overflow-hidden shadow-lg group cursor-pointer h-80"
                >
                  <img 
                    src={useCase.image} 
                    alt={useCase.title}
                    className="w-full h-full object-cover object-center transform group-hover:scale-110 transition-transform duration-500"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/50 to-transparent"></div>
                  <div className="absolute inset-0 flex flex-col justify-end p-6">
                    <div className="text-white mb-3">{useCase.icon}</div>
                    <h3 className="text-2xl font-bold text-white mb-2">{useCase.title}</h3>
                    <p className="text-white/90 text-sm">{useCase.description}</p>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* Humanitarian Mission Section */}
        <section className="py-16 md:py-20 bg-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid lg:grid-cols-2 gap-12 items-center">
              <motion.div
                initial={{ opacity: 0, x: -30 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
              >
                <h2 className="text-3xl md:text-4xl font-bold font-lato mb-6" style={{ color: 'var(--petition-dark)' }}>
                  A Humanitarian Infrastructure for the <span className="text-transparent bg-clip-text bg-gradient-to-r from-primary-blue to-primary-light">Web3 Era</span>
                </h2>
                <p className="text-base md:text-lg text-gray-700 leading-relaxed mb-4">
                  Petition.io is built to empower the most vulnerable—those whose voices are systematically ignored or suppressed by centralized systems. We're solving real-world injustices that traditional platforms fail to address.
                </p>
                <p className="text-base md:text-lg text-gray-700 leading-relaxed mb-6">
                  As a public-good platform, we enable anyone, anywhere—especially those most at risk—to raise their voice, organize support, and build collective power, free from censorship or bureaucratic theft.
                </p>
                <div className="flex flex-wrap gap-3">
                  <div className="px-4 py-2 bg-blue-50 border border-blue-200 rounded-full">
                    <span className="text-sm font-semibold text-primary-blue">No Censorship</span>
                  </div>
                  <div className="px-4 py-2 bg-green-50 border border-green-200 rounded-full">
                    <span className="text-sm font-semibold text-green-700">Public Good</span>
                  </div>
                  <div className="px-4 py-2 bg-purple-50 border border-purple-200 rounded-full">
                    <span className="text-sm font-semibold text-purple-700">Community Owned</span>
                  </div>
                </div>
              </motion.div>

              <motion.div
                initial={{ opacity: 0, x: 30 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                className="relative"
              >
                <div className="relative rounded-2xl overflow-hidden shadow-2xl">
                  <img 
                    src="/images/Humanitarian-Infrastructure.jpg" 
                    alt="Global Community" 
                    className="w-full h-96 object-cover object-center"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent"></div>
                  <div className="absolute bottom-6 left-6 right-6">
                    <div className="bg-white/95 backdrop-blur-sm rounded-xl p-4">
                      <p className="text-sm font-bold text-neutral-dark mb-1">Global Impact</p>
                      <p className="text-xs text-gray-600">Empowering voices from every corner of the world</p>
                    </div>
                  </div>
                </div>
              </motion.div>
            </div>
          </div>
        </section>

        {/* How Smart Contracts Work Section */}
        <section className="pt-16 pb-16 md:pb-20 bg-gradient-to-br from-gray-50 to-blue-50/30">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="text-center mb-12"
            >
              <h2 className="text-3xl md:text-4xl font-bold font-lato mb-4" style={{ color: 'var(--petition-dark)' }}>
                How <span className="text-gradient">Smart Contracts</span> Protect Your Petitions
              </h2>
              <p className="text-base md:text-lg text-gray-600 max-w-3xl mx-auto">
                Every action on Petition.io is powered by Ethereum smart contracts—self-executing code that operates without intermediaries
              </p>
            </motion.div>

            <div className="grid md:grid-cols-3 gap-6">
              {[
                {
                  title: 'Create & Publish',
                  description: 'When you create a petition, it\'s recorded on the Ethereum blockchain through our Petition Core smart contract. Once published, the petition data becomes permanent and publicly accessible.',
                  icon: '📝',
                  color: 'from-blue-500 to-cyan-500'
                },
                {
                  title: 'Sign & Support',
                  description: 'Every signature is cryptographically verified through your wallet address. The smart contract records each signature with a timestamp, creating an immutable chain of support.',
                  icon: '✍️',
                  color: 'from-purple-500 to-pink-500'
                },
                {
                  title: 'Fund & Distribute',
                  description: 'Contributions are held securely in the smart contract. When a petition reaches its goal, funds are automatically distributed to the creator—no middleman, no delays, no corruption.',
                  icon: '💎',
                  color: 'from-green-500 to-emerald-500'
                }
              ].map((step, idx) => (
                <motion.div
                  key={idx}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: idx * 0.1 }}
                  className="bg-white rounded-xl p-6 shadow-lg hover:shadow-xl transition-shadow"
                >
                  <div className={`w-16 h-16 rounded-xl bg-gradient-to-br ${step.color} flex items-center justify-center text-3xl mb-4`}>
                    {step.icon}
                  </div>
                  <h3 className="text-xl font-bold text-neutral-dark mb-3">{step.title}</h3>
                  <p className="text-sm md:text-base text-gray-600 leading-relaxed">{step.description}</p>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* Platform Comparison Section */}
        <section className="py-16 md:py-20 bg-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="text-center mb-12"
            >
              <h2 className="text-3xl md:text-4xl font-bold font-lato mb-4" style={{ color: 'var(--petition-dark)' }}>
                Why Petition.io is <span className="text-transparent bg-clip-text bg-gradient-to-r from-primary-blue to-primary-light">Different</span>
              </h2>
              <p className="text-base md:text-lg text-gray-600 max-w-3xl mx-auto">
                Unlike centralized platforms such as Change.org—which raised millions while maintaining control over data and decisions
              </p>
            </motion.div>

            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
              {[
                {
                  title: 'Public Protocol',
                  description: 'Built as a public protocol, not a private company',
                  icon: '🌐'
                },
                {
                  title: 'Verifiable Ownership',
                  description: 'Users have verifiable ownership and transparency',
                  icon: '🔐'
                },
                {
                  title: 'Tamper-Proof',
                  description: 'Every action is secured, traceable, and tamper-proof',
                  icon: '🛡️'
                },
                {
                  title: 'People First',
                  description: 'Petitions belong to the people, not corporations',
                  icon: '👥'
                }
              ].map((feature, idx) => (
                <motion.div
                  key={idx}
                  initial={{ opacity: 0, scale: 0.9 }}
                  whileInView={{ opacity: 1, scale: 1 }}
                  viewport={{ once: true }}
                  transition={{ delay: idx * 0.1 }}
                  whileHover={{ y: -5 }}
                  className="bg-gradient-to-br from-blue-50 to-white border border-blue-100 rounded-xl p-6 text-center"
                >
                  <div className="text-4xl mb-3">{feature.icon}</div>
                  <h3 className="text-lg font-bold text-neutral-dark mb-2">{feature.title}</h3>
                  <p className="text-sm text-gray-600">{feature.description}</p>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        <section className="py-20 relative overflow-hidden" style={{ backgroundColor: '#1e3a8a' }}>
          <div className="absolute inset-0 bg-gradient-to-br from-blue-900/60 via-blue-800/70 to-purple-900/60"></div>
          <div className="absolute inset-0 opacity-5">
            <img src="/images/image9.jpg" alt="" className="w-full h-full object-cover" />
          </div>
          <div className="relative z-10 max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
            >
              <h2 className="text-4xl md:text-5xl font-bold font-lato text-white mb-6">
                Ready to Make Your Voice Heard?
              </h2>
              <p className="text-xl text-white/90 mb-8">
                Join thousands of activists creating real change with blockchain-powered petitions
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <a href="https://app.petition.io" target="_blank" rel="noopener noreferrer">
                  <motion.button
                    whileHover={{ scale: 1.05, boxShadow: '0 8px 30px rgba(0, 0, 0, 0.3)' }}
                    whileTap={{ scale: 0.95 }}
                    className="px-8 py-4 bg-white text-primary-blue text-lg font-semibold rounded-xl shadow-lg inline-flex items-center justify-center"
                  >
                    Get Started Now
                    <svg className="ml-2 w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7l5 5m0 0l-5 5m5-5H6" />
                    </svg>
                  </motion.button>
                </a>
                <Link to="/how-it-works">
                  <motion.button
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    className="w-full px-8 py-4 bg-transparent text-white text-lg font-semibold rounded-xl border-2 border-white hover:bg-white hover:text-ethereum transition-all duration-300"
                  >
                    Explore Features
                  </motion.button>
                </Link>
              </div>
            </motion.div>
          </div>
        </section>
      </div>
    </>
  );
};

export default HomePage;
